import { useEffect, useRef, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import {
  CameraIcon, PolaroidIcon, HeartIcon, SmileIcon, GiftIcon,
  CherrySwirl, Spark, WaveDivider, CircularStamp,
  BrandChip, BrandFloat, BRAND, BigWordmark, InlineWordmark,
} from "@/components/Brand";
import { MEDIA } from "@/lib/media";

// Selected portfolio photos that double as an Instagram preview grid.
// Each tile links to the real IG profile so the user always lands on the latest content.
const INSTAGRAM_FEED = [
  "https://customer-assets.emergentagent.com/job_cerenza-studio/artifacts/foim52ht_Ana%20embarazo%20Andr%C3%A9s%20-%20Redes%20sociales-11.jpg",
  "https://customer-assets.emergentagent.com/job_cerenza-studio/artifacts/u1ifvw2q_Michelle-19.JPG",
  "https://customer-assets.emergentagent.com/job_cerenza-studio/artifacts/gcju0703_Antonella%201%20a%C3%B1o%201.png",
  "https://customer-assets.emergentagent.com/job_cerenza-studio/artifacts/3s6r84vl_Capuchino7.jpeg",
  "https://customer-assets.emergentagent.com/job_cerenza-studio/artifacts/eef2bxgk_Familia%20Arce%20%20Redes%20Sociales-%20RalvasaStudio_6.jpg",
  "https://customer-assets.emergentagent.com/job_cerenza-studio/artifacts/3puv0r2i_WhatsApp%20Image%202026-05-14%20at%205.19.02%20PM%20%284%29.jpeg",
];

/* ------------------ reveal helper ------------------ */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-[1000ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ------------------ static content ------------------ */
const VALUES = [
  { icon: BRAND.viewfinderPink, title: "Experiencias personalizadas", body: "Cada detalle pensado para contar tu historia de forma única." },
  { icon: BRAND.cameraPink, title: "Fotografía con alma", body: "Imágenes auténticas llenas de emoción, luz y conexión." },
  { icon: BRAND.polaroidsCream, title: "Un espacio para disfrutar", body: "Ambiente cálido, relajado y divertido para toda la familia." },
  { icon: BRAND.cameraRed, title: "Recuerdos que perduran", body: "Productos de alta calidad para conservar tus momentos favoritos." },
];

const TESTIMONIALS = [
  {
    quote: "Vivir una sesión en Cerenza fue como regalarle a mi familia un día de magia pura. Las fotos hablan por sí solas.",
    author: "Valentina R.",
    note: "Sesión familiar",
  },
  {
    quote: "Esperé toda mi maternidad este momento. Salí con el corazón lleno y un álbum que no me canso de mirar.",
    author: "Camila A.",
    note: "Sesión de maternidad",
  },
  {
    quote: "Pensé que sería sólo una foto. Fue una mañana entera de risas, mimos y momentos que no quiero olvidar.",
    author: "Mariana P.",
    note: "Sesión bebé",
  },
];

/* ------------------ landing ------------------ */
export default function Landing() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [defaultExp, setDefaultExp] = useState("");

  const openBooking = (exp = "") => {
    setDefaultExp(exp);
    setBookingOpen(true);
  };

  return (
    <main className="bg-cerenza-cream text-cerenza-ink overflow-x-clip" data-testid="landing-page">
      <Nav onBook={() => openBooking("")} />

      {/* HERO ============================================================ */}
      <section id="inicio" className="relative pt-28 md:pt-32 pb-16 md:pb-24" data-testid="hero-section">
        {/* BIG BRAND BANNER */}
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 pb-8 md:pb-12 text-center">
          <BigWordmark className="h-32 sm:h-40 md:h-52 lg:h-64" />
          <div className="mt-6 flex items-center justify-center gap-4 max-w-md mx-auto">
            <span className="block h-px flex-1 bg-cerenza-cherry/30" />
            <CherrySwirl color="#cd2e2e" className="w-6 h-6 shrink-0" />
            <span className="block h-px flex-1 bg-cerenza-cherry/30" />
          </div>
        </div>

        {/* floating brand icons */}
        <BrandFloat src={BRAND.cameraRed} className="hidden md:flex absolute top-[40%] left-[3%] w-16 h-16 opacity-90 animate-float" />
        <BrandFloat src={BRAND.polaroidsCream} className="hidden md:flex absolute top-[42%] right-[4%] w-20 h-20 opacity-90 animate-float" chip="bg-cerenza-pink" />
        <BrandFloat src={BRAND.viewfinderPink} className="hidden md:flex absolute bottom-[10%] left-[8%] w-14 h-14 opacity-80 animate-float" />

        <div className="max-w-[1320px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="flex items-center gap-3">
              <CameraIcon color="#cd2e2e" className="w-6 h-6" />
              <span className="eyebrow">Experiencias visuales</span>
            </div>
            <h2 className="font-display text-[44px] sm:text-6xl md:text-[72px] leading-[0.95] text-cerenza-cherry mt-5 text-balance">
              Para celebrar
              <br />
              lo que <em className="not-italic font-script text-[1.1em] text-cerenza-cherry">amas</em>
              <Spark className="inline-block w-7 h-7 align-top -mt-1 ml-1" />
            </h2>
            <p className="mt-7 text-base md:text-lg text-cerenza-ink/70 max-w-md leading-relaxed">
              Fotografía familiar con corazón, creatividad y una experiencia diseñada para crear recuerdos que perduran por siempre.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="hero-cta" onClick={() => openBooking("")} className="btn-cherry">
                Reserva tu experiencia →
              </button>
              <button
                data-testid="hero-secondary"
                onClick={() => document.getElementById("sesiones")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-cherry-outline"
              >
                Conoce las sesiones
              </button>
            </div>
          </div>

          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            {/* Pink curved blob behind image */}
            <div
              className="absolute -inset-6 md:-inset-10 bg-cerenza-pink rounded-[280px_180px_240px_220px/220px_240px_180px_280px] -z-0"
              aria-hidden="true"
            />
            <div className="relative z-10 overflow-hidden rounded-[60px_24px_60px_24px] shadow-[0_30px_60px_-24px_rgba(220,45,63,0.35)]">
              <img
                src={MEDIA.hero}
                alt="Familia feliz en estudio Cerenza"
                className="w-full h-[480px] md:h-[560px] object-cover photo-warm"
              />
            </div>
            {/* Circular stamp overlapping */}
            <div className="absolute -bottom-6 -right-2 md:-bottom-8 md:right-6 z-20">
              <CircularStamp className="w-28 h-28 md:w-36 md:h-36" />
            </div>
          </div>
        </div>
      </section>

      <WaveDivider from="#ffffd7" to="#f9d4d3" />

      {/* VALUES BAND ===================================================== */}
      <section id="experiencia" className="bg-cerenza-pinklight" data-testid="values-section">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-24">
          <Reveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
            {VALUES.map(({ icon, title, body }, i) => (
              <div key={title} className="flex flex-col" data-testid={`value-${i}`}>
                <BrandChip src={icon} className="w-14 h-14" />
                <h3 className="mt-5 font-display text-[15px] uppercase tracking-[0.12em] text-cerenza-cherry leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-cerenza-ink/70 leading-relaxed text-[15px]">{body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <WaveDivider from="#f9d4d3" to="#ffffd7" />

      {/* "MÁS QUE UNA SESIÓN" ============================================ */}
      <section className="bg-cerenza-cream relative" data-testid="experience-section">
        <BrandFloat src={BRAND.cameraPink} className="hidden md:flex absolute top-16 left-[4%] w-16 h-16 opacity-90 animate-float" />
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: big image with cherry blob */}
          <Reveal className="lg:col-span-5 relative">
            <div className="absolute -inset-5 md:-inset-8 bg-cerenza-pink rounded-[200px_280px_240px_180px/220px_180px_280px_220px] -z-0" aria-hidden="true" />
            <div className="relative z-10 overflow-hidden rounded-[50px_24px_50px_24px] shadow-[0_24px_60px_-24px_rgba(205,46,46,0.35)] bg-cerenza-pinklight">
              <img src={MEDIA.experience} alt="Familia Cerenza" className="w-full h-[420px] md:h-[520px] object-contain photo-warm" />
            </div>
          </Reveal>

          {/* Middle: copy */}
          <Reveal delay={120} className="lg:col-span-4">
            <p className="eyebrow flex items-center gap-2">
              <CameraIcon color="#cd2e2e" className="w-5 h-5" /> Nuestra experiencia
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[1.02] text-cerenza-ink text-balance">
              Más que una sesión,
              <br /> es una experiencia
              <br /> que
              <span className="relative inline-block ml-2">
                <span className="absolute inset-x-0 bottom-1 h-3 bg-cerenza-pink rounded-full -z-0" />
                <span className="relative font-script text-cerenza-cherry text-[1.05em]">recordarás</span>
              </span>
              <span className="text-cerenza-cherry">.</span>
            </h2>
            <p className="mt-7 text-cerenza-ink/70 text-base md:text-lg leading-relaxed max-w-md">
              Te acompañamos desde el primer momento para que vivas una experiencia llena de magia,
              diversión y conexión. Nos encargamos de todo para que tú solo disfrutes y nosotros
              capturemos lo más valioso.
            </p>
            <button
              data-testid="experience-cta"
              onClick={() => document.getElementById("sesiones")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-8 inline-flex items-center gap-2 font-semibold uppercase tracking-[0.18em] text-xs text-cerenza-cherry hover:gap-4 transition-all"
            >
              Conoce cómo lo hacemos →
            </button>
          </Reveal>

          {/* Right: polaroid collage */}
          <Reveal delay={220} className="lg:col-span-3 relative h-[360px] md:h-[440px]">
            {MEDIA.polaroids.map((src, i) => (
              <div
                key={src}
                className={`polaroid absolute w-40 md:w-48 transition-transform duration-500 hover:rotate-0 hover:scale-105 ${
                  i === 0 ? "top-0 right-6 rotate-[10deg]" :
                  i === 1 ? "top-32 left-0 -rotate-[8deg]" :
                  "bottom-0 right-2 rotate-[6deg]"
                }`}
              >
                <img src={src} alt="" className="w-full h-44 md:h-52 object-cover photo-warm" />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <WaveDivider from="#ffffd7" to="#f9d4d3" />

      {/* SOBRE MÍ ========================================================= */}
      <section id="sobre-mi" className="bg-cerenza-pinklight relative overflow-hidden" data-testid="about-section">
        <BrandFloat src={BRAND.polaroidsCream} className="hidden md:flex absolute top-20 right-[6%] w-20 h-20 opacity-90 animate-float" chip="bg-cerenza-pink" />
        <BrandFloat src={BRAND.cameraRed} className="hidden md:flex absolute bottom-20 left-[4%] w-14 h-14 opacity-80 animate-float" />

        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Photo slot */}
          <Reveal className="lg:col-span-5 relative">
            <div className="absolute -inset-4 md:-inset-6 bg-cerenza-cream rounded-[200px_120px_180px_140px/180px_140px_220px_160px] -z-0" aria-hidden="true" />
            <div className="relative z-10 overflow-hidden rounded-[60px_24px_60px_24px] shadow-[0_24px_50px_-20px_rgba(220,45,63,0.3)]">
              {/* TODO: reemplazar src con la foto personal de Cerenza */}
              <img
                src={MEDIA.aboutMe}
                alt="Detrás de Cerenza"
                className="w-full h-[480px] md:h-[560px] object-cover object-center photo-warm"
              />
            </div>
            <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 z-20 bg-cerenza-cherry text-cerenza-creamlight font-script text-2xl md:text-3xl rounded-full w-28 h-28 md:w-32 md:h-32 flex items-center justify-center rotate-[-8deg] shadow-[0_12px_30px_-8px_rgba(220,45,63,0.5)] text-center leading-tight px-3">
              ¡hola,<br/>soy yo!
            </div>
          </Reveal>

          {/* Copy */}
          <Reveal delay={120} className="lg:col-span-7">
            <p className="eyebrow flex items-center gap-2">
              <HeartIcon color="#cd2e2e" className="w-5 h-5" /> Sobre mí
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[1.02] text-cerenza-ink text-balance">
              Detrás de <span className="font-script text-cerenza-cherry text-[1.08em]">Cerenza</span>
              <span className="text-cerenza-cherry">.</span>
            </h2>

            <div className="mt-8 space-y-5 text-cerenza-ink/75 text-base md:text-[17px] leading-[1.7] max-w-2xl">
              <p>
                Detrás de <strong className="text-cerenza-cherry font-semibold">CERENZA</strong> está
                mi amor por los recuerdos, las emociones reales y esos pequeños momentos que muchas
                veces pasan demasiado rápido.
              </p>
              <p>
                Con el tiempo entendí que la fotografía familiar no debería sentirse como una sesión
                tradicional o una obligación frente a la cámara, sino como una experiencia especial:
                un espacio para conectar, disfrutar y celebrar a las personas que más amas.
              </p>
              <p className="font-display text-2xl md:text-3xl text-cerenza-cherry leading-snug !mt-7">
                Por eso creé <span className="font-script text-[1.1em]">Cerenza</span>.
              </p>
              <p>
                Un lugar donde cada detalle está pensado para hacerte sentir cómoda, acompañada y
                verdaderamente especial. Desde la planeación de tu sesión hasta la entrega final,
                mi intención es que vivas una experiencia cálida, personalizada y exclusiva,
                creando imágenes que no solo se vean bonitas, sino que <em className="text-cerenza-cherry not-italic font-medium">realmente se sientan tuyas</em>.
              </p>
              <p>
                Más que retratos, busco crear recuerdos con intención, sensibilidad y una estética
                cuidada que transforme momentos cotidianos en memorias que permanecerán contigo por años.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <button data-testid="about-cta" onClick={() => openBooking("")} className="btn-cherry">
                Conoceremos tu historia →
              </button>
              <p className="font-script text-2xl text-cerenza-cherry">— con cariño</p>
            </div>
          </Reveal>
        </div>
      </section>

      <WaveDivider from="#f9d4d3" to="#f9d4d3" />

      {/* SESSIONS — arched cards ========================================= */}
      <section id="sesiones" className="bg-cerenza-pinklight relative" data-testid="sessions-section">
        <BrandFloat src={BRAND.polaroidsRed} className="hidden md:flex absolute top-10 right-[6%] w-16 h-16 opacity-90 animate-float" />
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className="eyebrow flex items-center justify-center gap-2">
              <CameraIcon color="#cd2e2e" className="w-5 h-5" /> Sesiones
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 text-cerenza-ink leading-[1.05] text-balance">
              Cada etapa
              <span className="relative inline-block ml-3">
                <span className="absolute inset-x-0 bottom-1 h-3 bg-cerenza-pink rounded-full" />
                <span className="relative font-script text-cerenza-cherry text-[1.05em]">merece</span>
              </span>
              {" "}ser celebrada.
            </h2>
            <p className="mt-6 text-cerenza-ink/70 max-w-xl mx-auto">
              Siete experiencias diseñadas para acompañarte en los momentos más especiales.
              Cuéntanos cuál es el tuyo.
            </p>
          </Reveal>

          <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-7">
            {MEDIA.sessions.map((s, i) => (
              <Reveal key={s.id} delay={i * 90}>
                <button
                  type="button"
                  onClick={() => openBooking(s.id)}
                  data-testid={`exp-card-${s.id}`}
                  data-category={s.id}
                  className="group block w-full text-left bg-cerenza-cream arch-card p-3 pb-6 hover:-translate-y-2 transition-transform duration-500 shadow-[0_18px_36px_-22px_rgba(220,45,63,0.35)]"
                >
                  <div className={`overflow-hidden arch-card aspect-[3/4] ${s.fit === "contain" ? "bg-cerenza-pinklight" : "bg-cerenza-pink"}`}>
                    <img
                      src={s.url}
                      alt={s.title}
                      className={`w-full h-full ${s.fit === "contain" ? "object-contain" : "object-cover"} transition-transform duration-700 group-hover:scale-105 photo-warm`}
                    />
                  </div>
                  <div className="mt-5 px-2 flex items-center justify-center -mt-9 relative">
                    <span className="bg-cerenza-cream rounded-full w-12 h-12 flex items-center justify-center shadow-[0_8px_18px_-10px_rgba(220,45,63,0.5)] border border-cerenza-pink/40">
                      <CameraIcon color="#cd2e2e" className="w-5 h-5" />
                    </span>
                  </div>
                  <h3 className="mt-3 text-center font-display text-[15px] uppercase tracking-[0.14em] text-cerenza-cherry">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-center text-sm text-cerenza-ink/65 leading-snug px-2">{s.subtitle}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICIOS EXTRAS ================================================== */}
      <section id="extras" className="bg-cerenza-cream relative overflow-hidden" data-testid="extras-section">
        <BrandFloat src={BRAND.cameraPink} className="hidden md:flex absolute top-16 left-[6%] w-14 h-14 opacity-80 animate-float" />
        <BrandFloat src={BRAND.viewfinderPink} className="hidden md:flex absolute bottom-16 right-[8%] w-16 h-16 opacity-85 animate-float" chip="bg-cerenza-pink" />
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="max-w-3xl">
            <p className="eyebrow flex items-center gap-2">
              <GiftIcon color="#cd2e2e" className="w-5 h-5" /> Servicios complementarios
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 text-cerenza-ink leading-[1.02] text-balance">
              Para que solo te ocupes de
              <span className="relative inline-block ml-3">
                <span className="absolute inset-x-0 bottom-1 h-3 bg-cerenza-pink rounded-full" />
                <span className="relative font-script text-cerenza-cherry text-[1.05em]">disfrutar</span>
              </span>
              <span className="text-cerenza-cherry">.</span>
            </h2>
            <p className="mt-6 text-cerenza-ink/70 max-w-xl text-base md:text-lg leading-relaxed">
              Cuidamos cada detalle para que tu experiencia sea completa.
              Estos servicios pueden integrarse a cualquier sesión, según disponibilidad y previo acuerdo.
            </p>
          </Reveal>

          <div className="mt-14 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: BRAND.polaroidsCream, title: "Préstamo de vestuario", body: "Acceso a una selección curada de prendas y accesorios para tu sesión." },
              { icon: BRAND.cameraRed, title: "Locación, estudio o domicilio", body: "Llevamos la experiencia al espacio que se sienta más tuyo." },
              { icon: BRAND.viewfinderPink, title: "Maquillaje y peinado profesional", body: "Look diseñado a tu medida por especialistas que entienden la cámara." },
              { icon: BRAND.cameraPink, title: "Diseño de vestuario personalizado", body: "Piezas creadas a medida con una diseñadora profesional para tu sesión." },
              { icon: BRAND.polaroidsRed, title: "Impresiones y enmarcados", body: "Tus imágenes en papel fine art, listas para colgar y conservar." },
              { icon: BRAND.cameraRed, title: "Dirección creativa a medida", body: "Concepto, set y atmósfera diseñados alrededor de tu historia." },
            ].map(({ icon, title, body }, i) => (
              <Reveal key={title} delay={i * 70}>
                <div className="h-full bg-cerenza-pinklight rounded-[28px] p-7 md:p-8 hover:-translate-y-1.5 hover:bg-cerenza-pink/70 transition-all duration-500" data-testid={`extra-${i}`}>
                  <BrandChip src={icon} className="w-14 h-14" />
                  <h3 className="mt-5 font-display text-2xl text-cerenza-ink leading-tight">{title}</h3>
                  <p className="mt-3 text-cerenza-ink/70 text-[15px] leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between bg-cerenza-cream border border-cerenza-pink/60 rounded-[28px] p-6 md:p-8">
            <div className="flex items-start gap-4">
              <Spark color="#cd2e2e" className="w-7 h-7 mt-1 shrink-0" />
              <div>
                <p className="font-display text-xl md:text-2xl text-cerenza-ink leading-snug">
                  ¿Tienes una idea distinta? Cuéntanosla.
                </p>
                <p className="mt-1.5 text-sm text-cerenza-ink/65">
                  Cada experiencia se cotiza según disponibilidad y los detalles que quieras incluir.
                </p>
              </div>
            </div>
            <button
              data-testid="extras-cta"
              onClick={() => openBooking("personalizada")}
              className="btn-cherry shrink-0"
            >
              Diseñar mi sesión →
            </button>
          </Reveal>
        </div>
      </section>

      {/* CTA RED BAND ==================================================== */}
      <section className="relative bg-cerenza-cherry text-cerenza-creamlight overflow-hidden" data-testid="cta-band">
        <BrandFloat src={BRAND.polaroidsCream} className="absolute top-8 left-[8%] w-16 h-16 opacity-80" chip="bg-cerenza-cream" />
        <BrandFloat src={BRAND.cameraPink} className="absolute bottom-10 right-[18%] w-16 h-16 opacity-80" chip="bg-cerenza-cream" />
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="font-script text-3xl text-cerenza-pink">— para siempre —</p>
            <h2 className="font-display text-5xl md:text-6xl mt-3 leading-[1.02] text-balance">
              Algunos momentos merecen
              <br /> quedarse para siempre.
            </h2>
            <button
              data-testid="band-cta"
              onClick={() => openBooking("")}
              className="mt-10 inline-flex items-center gap-3 px-9 py-4 bg-cerenza-cream text-cerenza-cherry rounded-full font-semibold uppercase tracking-[0.14em] text-xs hover:bg-cerenza-pink transition-all duration-300 hover:-translate-y-0.5"
            >
              Reserva tu experiencia →
            </button>
          </div>
          <div className="md:col-span-5 relative">
            <div className="relative overflow-hidden rounded-[40px_120px_40px_120px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.4)] rotate-2 bg-cerenza-cream">
              <img src={MEDIA.finalCta} alt="Momento Cerenza" className="w-full h-[360px] md:h-[440px] object-contain photo-warm" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS ==================================================== */}
      <section className="bg-cerenza-cream relative" data-testid="testimonials-section">
        <BrandFloat src={BRAND.cameraRed} className="hidden md:flex absolute top-16 right-[6%] w-14 h-14 opacity-80 animate-float" />
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="eyebrow flex items-center justify-center gap-2">
              <HeartIcon color="#cd2e2e" className="w-5 h-5" /> Voces de familia
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 text-cerenza-ink leading-[1.05] text-balance">
              Lo que nuestras familias
              <span className="font-script text-cerenza-cherry text-[1.05em]"> recuerdan</span>.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.author} delay={i * 100}>
                <figure className={`bg-cerenza-pinklight rounded-[36px] p-7 md:p-8 h-full flex flex-col ${i === 1 ? "md:-mt-6" : ""}`}>
                  <Spark color="#cd2e2e" className="w-6 h-6" />
                  <blockquote className="mt-4 font-display text-xl md:text-[22px] leading-snug text-cerenza-ink text-pretty">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 text-sm">
                    <span className="h-px w-8 bg-cerenza-cherry" />
                    <span className="font-semibold text-cerenza-cherry">{t.author}</span>
                    <span className="text-cerenza-ink/60">· {t.note}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM ======================================================= */}
      <section id="instagram" className="bg-cerenza-cream relative overflow-hidden" data-testid="instagram-section">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="eyebrow flex items-center justify-center gap-2">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#cd2e2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="#cd2e2e" stroke="none" />
              </svg>
              Síguenos en Instagram
            </p>
            <h2 className="font-display text-5xl md:text-6xl mt-4 text-cerenza-ink leading-[1.05] text-balance">
              Más momentos
              <span className="font-script text-cerenza-cherry text-[1.05em]"> en nuestro feed</span>.
            </h2>
            <a
              href="https://www.instagram.com/cerenza_familyphotoroom"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-handle"
              className="inline-flex items-center gap-2 mt-5 font-semibold text-cerenza-cherry hover:text-cerenza-cherry/70 transition-colors"
            >
              @cerenza_familyphotoroom
            </a>
          </Reveal>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {INSTAGRAM_FEED.map((src, i) => (
              <a
                key={src}
                href="https://www.instagram.com/cerenza_familyphotoroom"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-tile-${i}`}
                className="group relative block aspect-square overflow-hidden rounded-2xl bg-cerenza-pinklight shadow-[0_8px_24px_-16px_rgba(220,45,63,0.45)]"
              >
                <img
                  src={src}
                  alt="Cerenza en Instagram"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <span className="absolute inset-0 bg-cerenza-cherry/0 group-hover:bg-cerenza-cherry/35 transition-colors duration-300 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-cerenza-cream opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </span>
              </a>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="https://www.instagram.com/cerenza_familyphotoroom"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-cta"
              className="btn-cherry inline-flex items-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
              </svg>
              Síguenos en Instagram
            </a>
          </div>
        </div>
      </section>

      <Footer onBook={() => openBooking("")} />

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        defaultExperience={defaultExp}
      />
    </main>
  );
}
